import { useEffect, useState } from "react";
import {
  Building2, Users, UserCog, AlertTriangle, ShieldCheck, CalendarClock, Wrench,
  MapPin, X, Loader2, ArrowRight,
} from "lucide-react";
import api from "@/lib/api";
import { PageHeader, ScoreRing, PriorityBadge, scoreColor } from "@/components/common/ui";
import { cn } from "@/lib/utils";

const TYPE_COLOR = { "Group Home": "#155EEF", "Day Program": "#12B76A" };
const occColor = (o) => (o >= 95 ? "#F04438" : o >= 80 ? "#F79009" : "#12B76A");

export default function Programs() {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => { api.get("/programs/overview").then((r) => setData(r.data)).catch(() => setData({ programs: [], regions: [], totals: {} })); }, []);

  const openDetail = async (pid) => {
    setDetailLoading(true); setDetail({ loading: true });
    try { const { data } = await api.get(`/programs/${pid}`); setDetail(data); }
    catch (e) { setDetail(null); }
    finally { setDetailLoading(false); }
  };

  if (!data) return <div className="flex h-64 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div>;
  const t = data.totals;

  return (
    <div>
      <PageHeader overline="Workforce · Programs & Facilities" title="Programs & Facilities"
        description="Region → program → home hierarchy on shared identity, with live capacity, occupancy and program scorecards." />

      {/* Totals */}
      <div className="mb-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[{ l: "Programs", v: t.programs, i: Building2 }, { l: "Residents", v: t.residents, i: Users },
          { l: "Staff", v: t.staff, i: UserCog }, { l: "Occupancy", v: `${t.occupancy}%`, i: MapPin }].map((k) => (
          <div key={k.l} data-testid={`programs-total-${k.l.toLowerCase()}`} className="flex items-center gap-3 rounded-xl border bg-card p-4 shadow-sm">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#155EEF]/10 text-[#155EEF]"><k.i size={18} /></span>
            <div><div className="text-2xl font-bold tracking-tight">{k.v}</div><div className="overline">{k.l}</div></div>
          </div>
        ))}
      </div>

      {/* Region rollups */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {data.regions.map((r) => (
          <div key={r.region} className="rounded-xl border bg-card p-4 shadow-sm">
            <div className="flex items-center gap-2 text-sm font-bold"><MapPin size={15} className="text-[#7C3AED]" /> {r.region}</div>
            <div className="mt-2 flex items-center justify-between text-sm text-muted-foreground">
              <span>{r.programs} programs · {r.residents}/{r.capacity} beds</span>
              <span className="font-semibold" style={{ color: occColor(r.occupancy) }}>{r.occupancy}%</span>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full" style={{ width: `${r.occupancy}%`, backgroundColor: occColor(r.occupancy) }} /></div>
          </div>
        ))}
      </div>

      {/* Program cards */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {data.programs.map((p) => (
          <button key={p.id} data-testid={`program-card-${p.id}`} onClick={() => openDetail(p.id)}
            className="group flex flex-col rounded-xl border bg-card p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#155EEF] hover:shadow-lg">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="rounded px-2 py-0.5 text-[10px] font-bold uppercase text-white" style={{ backgroundColor: TYPE_COLOR[p.type] || "#64748B" }}>{p.type}</span>
                  <span className="text-xs text-muted-foreground">{p.region}</span>
                </div>
                <h3 className="mt-1.5 text-base font-bold">{p.name}</h3>
                <div className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin size={12} /> {p.town}</div>
              </div>
              <ScoreRing value={p.health} size={58} color={scoreColor(p.health)} />
            </div>
            <div className="mt-3 flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Occupancy</span>
              <span className="font-semibold" style={{ color: occColor(p.occupancy) }}>{p.residents}/{p.capacity} ({p.occupancy}%)</span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full" style={{ width: `${Math.min(100, p.occupancy)}%`, backgroundColor: occColor(p.occupancy) }} /></div>
            <div className="mt-4 grid grid-cols-4 gap-2 text-center">
              {[{ i: UserCog, v: p.staff, l: "Staff", c: "#155EEF" }, { i: AlertTriangle, v: p.open_incidents, l: "Incidents", c: "#F79009" },
                { i: ShieldCheck, v: p.overdue_compliance, l: "Overdue", c: "#F04438" }, { i: CalendarClock, v: p.open_shifts, l: "Open Shifts", c: "#0EA5E9" }].map((s, i) => (
                <div key={i} className="rounded-lg border bg-background p-2">
                  <s.i size={14} className="mx-auto" style={{ color: s.c }} />
                  <div className="mt-0.5 text-base font-bold">{s.v}</div><div className="overline">{s.l}</div>
                </div>
              ))}
            </div>
            <span className="mt-3 flex items-center gap-1 text-xs font-semibold text-[#155EEF] opacity-0 transition-opacity group-hover:opacity-100">View program <ArrowRight size={13} /></span>
          </button>
        ))}
      </div>

      {detail && <ProgramDrawer detail={detail} loading={detailLoading} onClose={() => setDetail(null)} />}
    </div>
  );
}

function ProgramDrawer({ detail, loading, onClose }) {
  const p = detail.program;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="program-drawer" className="careiq-scroll relative h-full w-full max-w-xl overflow-y-auto border-l bg-card p-6 shadow-2xl">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <div className="overline">{p?.type} · {p?.region}</div>
            <h2 className="text-xl font-bold">{p?.name || "Program"}</h2>
            <div className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin size={12} /> {p?.town}</div>
          </div>
          <button data-testid="program-drawer-close" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={20} /></button>
        </div>
        {loading || detail.loading ? <div className="flex h-40 items-center justify-center text-muted-foreground"><Loader2 className="animate-spin" /></div> : (
          <>
            <div className="mb-5 grid grid-cols-3 gap-3">
              {[["Health", p.health], ["Occupancy", `${p.occupancy}%`], ["High-Risk", p.high_risk]].map(([l, v]) => (
                <div key={l} className="rounded-lg border bg-background p-3 text-center"><div className="text-xl font-bold">{v}</div><div className="overline">{l}</div></div>
              ))}
            </div>
            <Section title={`Residents (${detail.residents.length})`}>
              {detail.residents.map((r) => (
                <Row key={r.id} left={`${r.first_name} ${r.last_name}`} sub={`Room ${r.room || "—"}`} right={<PriorityBadge level={r.risk_level} />} />
              ))}
            </Section>
            <Section title={`Staff (${detail.staff.length})`}>
              {detail.staff.map((s) => <Row key={s.id} left={`${s.first_name} ${s.last_name}`} sub={s.title} />)}
            </Section>
            <Section title={`Recent Incidents (${detail.incidents.length})`}>
              {detail.incidents.map((i) => <Row key={i.id} left={i.type} sub={i.resident_name} right={<PriorityBadge level={i.severity} />} />)}
              {detail.incidents.length === 0 && <Empty>No incidents on record.</Empty>}
            </Section>
            <Section title={`Compliance (${detail.compliance.length})`}>
              {detail.compliance.map((c) => <Row key={c.id} left={c.requirement} sub={c.framework} right={<span className="text-xs font-semibold capitalize" style={{ color: c.status === "overdue" ? "#F04438" : c.status === "due-soon" ? "#F79009" : "#12B76A" }}>{c.status}</span>} />)}
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="mb-4">
    <div className="mb-2 text-sm font-bold">{title}</div>
    <div className="divide-y rounded-lg border">{children}</div>
  </div>
);
const Row = ({ left, sub, right }) => (
  <div className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
    <div className="min-w-0"><div className="truncate font-medium">{left}</div>{sub && <div className="truncate text-xs text-muted-foreground">{sub}</div>}</div>
    {right}
  </div>
);
const Empty = ({ children }) => <div className="px-3 py-4 text-center text-xs text-muted-foreground">{children}</div>;
